/* eslint-disable jsx-a11y/anchor-has-content */
// node modules
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// app modules: components
import UpgradeToPremium from 'components/UpgradeToPremium';

// app modules: services
import { voucherClaim } from 'services/route';

// app modules: props
import { SubscriptionProps } from 'components/Login/LoginProps';

class MembershipRenewal extends Component {
  static propTypes = {
    annualProduct: PropTypes.string,
    annualRenewalProduct: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    defaultSubscriptionPlanChoice: PropTypes.oneOf(['', 'annual', 'monthly']),
    extraInfo: PropTypes.node,
    headingClassName: PropTypes.string,
    hideBenefits: PropTypes.bool,
    monthlyProduct: PropTypes.string,
    monthlyRenewalProduct: PropTypes.string,
    overrideProduct: PropTypes.string,
    partner: PropTypes.string,
    renewalSuccessfulContent: PropTypes.node,
    renewTitle: PropTypes.node,
    renewToAnnualCode: PropTypes.string,
    requestedCoupon: PropTypes.string,
    subscription: SubscriptionProps,
    title: PropTypes.node,
    upgradeSuccessfulContent: PropTypes.node,
    voucherCheckout: PropTypes.shape({
      passthrough: PropTypes.string,
      annual: PropTypes.shape({
        paddleProductId: PropTypes.string,
      }),
      monthly: PropTypes.shape({
        paddleProductId: PropTypes.string,
      }),
    }),
    theme: PropTypes.string,
  };

  static defaultProps = {
    annualProduct: null,
    annualRenewalProduct: null,
    children: null,
    className: '',
    defaultSubscriptionPlanChoice: null,
    extraInfo: null,
    headingClassName: 'margin-bottom-10 margin-top-10',
    hideBenefits: false,
    monthlyProduct: null,
    monthlyRenewalProduct: null,
    overrideProduct: null,
    partner: null,
    renewalSuccessfulContent: null,
    renewTitle: 'Renew your ckbk Premium Membership',
    renewToAnnualCode: null,
    requestedCoupon: null,
    subscription: null,
    title: (
      <React.Fragment>
        Upgrade to ckbk <span style={{ whiteSpace: 'nowrap' }}>Premium Membership</span>
      </React.Fragment>
    ),
    upgradeSuccessfulContent: null,
    voucherCheckout: null,
  };

  render() {
    const {
      annualProduct,
      annualRenewalProduct,
      children,
      defaultSubscriptionPlanChoice,
      extraInfo,
      headingClassName,
      hideBenefits,
      monthlyProduct,
      monthlyRenewalProduct,
      overrideProduct,
      partner,
      renewalSuccessfulContent,
      renewTitle,
      renewToAnnualCode,
      requestedCoupon,
      subscription,
      title,
      upgradeSuccessfulContent,
      voucherCheckout,
      ...otherProps
    } = this.props;

    if (annualProduct && monthlyProduct) {
      if (voucherCheckout) {
        let extraPassthrough = {};
        if (voucherCheckout.passthrough) {
          try {
            extraPassthrough = JSON.parse(voucherCheckout.passthrough);
          } catch (err) {
            // Ignoring bad passthrough data
          }
        }
        return (
          <UpgradeToPremium
            annualPlanId={voucherCheckout.annual.paddleProductId}
            monthlyPlanId={voucherCheckout.monthly.paddleProductId}
            overridePlanId={voucherCheckout.monthly.paddleProductId}
            defaultCoupon={voucherCheckout.monthly.discounts?.[0]?.coupon}
            checkoutRoute={voucherClaim(voucherCheckout.id, partner)}
            title={title}
            extraInfo={extraInfo}
            extraPassthrough={extraPassthrough}
            completionContent={upgradeSuccessfulContent}
            buttonTextWithTrial="Claim Free Access"
            hideBenefits={hideBenefits}
            planChooserProps={{
              hideCoupon: true,
              hidePromotionalRateWarning: true,
              withZeroPriceTrials: true,
            }}
            {...otherProps}
          >
            {children}
          </UpgradeToPremium>
        );
      } else if (subscription && subscription.status === 'deleted') {
        let initialPlanId = overrideProduct;
        if (!initialPlanId || initialPlanId === annualProduct || initialPlanId === monthlyProduct) {
          switch (defaultSubscriptionPlanChoice) {
            case 'annual':
              initialPlanId = annualRenewalProduct;
              break;
            case 'monthly':
              initialPlanId = monthlyRenewalProduct;
              break;
            default:
            // Nothing
          }
        }
        return (
          <UpgradeToPremium
            annualPlanId={annualRenewalProduct}
            monthlyPlanId={monthlyRenewalProduct}
            overridePlanId={initialPlanId}
            defaultCoupon={requestedCoupon || renewToAnnualCode}
            extraInfo={
              subscription.is_valid ? (
                <p className={headingClassName}>
                  <strong>Renew now to ensure uninterrupted access.</strong>
                  <br /> When you renew, the remaining time on your existing membership will be
                  automatically carried over.
                </p>
              ) : null
            }
            hideBenefits={
              hideBenefits ||
              (subscription.is_valid &&
                subscription.provider !== 'paddle' &&
                subscription.provider !== 'apple' &&
                subscription.provider !== 'google')
            }
            title={renewTitle}
            checkoutCompleteMessage="Thanks for renewing your ckbk Premium Membership"
            completionContent={renewalSuccessfulContent}
            buttonText="Renew Subscription"
            {...otherProps}
          >
            {children}
          </UpgradeToPremium>
        );
      } else if (!subscription || subscription.is_new) {
        return (
          <UpgradeToPremium
            annualPlanId={annualProduct}
            monthlyPlanId={monthlyProduct}
            overridePlanId={overrideProduct}
            title={title}
            extraInfo={extraInfo}
            completionContent={upgradeSuccessfulContent}
            hideBenefits={hideBenefits}
            {...otherProps}
          >
            {children}
          </UpgradeToPremium>
        );
      }
    }
    return null;
  }
}

const mapStateToProps = state => {
  const { products } = state.config.paywall;

  return {
    subscription: state.user.subscription,
    renewToAnnualCode: state.config.paywall.discount.renewToAnnualCode,
    defaultSubscriptionPlanChoice: state.config.paywall.defaultSubscriptionPlanChoice,
    annualProduct: products.annual.paddleProductId,
    monthlyProduct: products.monthly.paddleProductId,
    annualRenewalProduct: products.annualRenewal.paddleProductId,
    monthlyRenewalProduct: products.monthlyRenewal.paddleProductId,
    overrideProduct: state.paywall.productId,
    platform: state.device.platform,
    voucherCheckout: state.user.voucher_checkout,
    requestedCoupon: state.paywall.requestedCoupon,
    partner: state.paywall.partner,
    theme: state.config.theme,
  };
};

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MembershipRenewal);
